import React, { useState, useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Box,
    Tabs,
    Tab,
    Autocomplete,
    Chip,
    Divider,
    Typography,
    Card,
    CardContent,
    CardActions,
    Grid,
    ListItemAvatar,
    Avatar
} from '@mui/material';
import { 
    Add as AddIcon, 
    Delete as DeleteIcon, 
    Edit as EditIcon,
    PersonAdd as PersonAddIcon,
    Email as EmailIcon,
    Group as GroupIcon,
    RemoveCircle as RemoveCircleIcon
} from '@mui/icons-material';
import GroupInvitationManager from './GroupInvitationManager';
import axiosInstance from '../utils/axiosConfig';
import EditGroupForm from './EditGroupForm';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const GroupManagement = ({ 
    groups, 
    availableUsers, 
    onAddGroup, 
    onDeleteGroup, 
    onEditGroup, 
    onAddMember, 
    onRemoveMember, 
    onInviteMember,
    onRefresh,
    isTeamLeader
}) => {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [memberDialogOpen, setMemberDialogOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupDescription, setNewGroupDescription] = useState('');
    const [tabValue, setTabValue] = useState(0);
    const [emailInvite, setEmailInvite] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState({});
    const [editMode, setEditMode] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);

    useEffect(() => {
        console.log('Available users in GroupManagement:', availableUsers);
    }, [availableUsers]);

    useEffect(() => {
        console.log('GroupManagement props:', {
            groupCount: groups?.length,
            availableUserCount: availableUsers?.length,
            selectedGroup: selectedGroup?.name,
            selectedUsers: selectedUsers?.length
        });
    }, [groups, availableUsers, selectedGroup, selectedUsers]);

    useEffect(() => {
        if (selectedGroup) {
            const updatedGroup = groups.find(g => g.id === selectedGroup.id);
            if (updatedGroup) {
                setSelectedGroup(updatedGroup);
            }
        }
    }, [groups]);

    const addMembersToGroup = async (groupId, userIds) => {
        try {
            const response = await axiosInstance.post(`/groups/${groupId}/members`, { userIds });
            return response.data;
        } catch (error) {
            console.error('Error adding members:', error);
            throw error;
        }
    };

    const handleSubmit = () => {
        if (newGroupName.trim()) {
            onAddGroup({
                name: newGroupName.trim(),
                description: newGroupDescription.trim()
            });
            setNewGroupName('');
            setNewGroupDescription('');
            setOpen(false);
        }
    };

    const handleOpenMemberDialog = (group) => {
        setSelectedGroup(group);
        setMemberDialogOpen(true);
    };

    const handleCloseMemberDialog = () => {
        setSelectedGroup(null);
        setMemberDialogOpen(false);
        setTabValue(0);
        setEmailInvite('');
        setSelectedUser(null);
        setSelectedUsers({});
    };

    const handleUserSelection = (groupId) => (event, selectedOptions) => {
        // Filter out any users that are already members of this group
        const currentGroup = groups.find(g => g.id === groupId);
        const currentMembers = new Set(
            (currentGroup?.members || []).map(member => member.id)
        );

        const filteredOptions = selectedOptions.filter(user => 
            !currentMembers.has(user.id)
        );

        if (filteredOptions.length !== selectedOptions.length) {
            console.log('Filtered out existing members:', {
                selected: selectedOptions.length,
                filtered: filteredOptions.length
            });
        }

        setSelectedUsers(prevState => ({
            ...prevState,
            [groupId]: filteredOptions
        }));
    };

    const handleAddMembers = async (groupId) => {
        try {
            const groupSelectedUsers = selectedUsers[groupId] || [];
            if (!groupSelectedUsers.length) {
                toast.error('Please select members to add');
                return;
            }

            console.log('Adding members to group:', {
                groupId,
                selectedUsers: groupSelectedUsers.map(u => ({
                    id: u.id,
                    name: `${u.first_name} ${u.last_name}`
                }))
            });

            await onAddMember(groupId, groupSelectedUsers);
            
            // Clear only this group's selections
            setSelectedUsers(prev => ({
                ...prev,
                [groupId]: []
            }));

            // Update the local state immediately
            setSelectedGroup(prev => {
                if (!prev || prev.id !== groupId) return prev;
                return {
                    ...prev,
                    members: [...prev.members, ...groupSelectedUsers]
                };
            });
            
            toast.success('Members added successfully');
            
            // Refresh in background
            onRefresh?.();
        } catch (error) {
            console.error('Error adding members:', error);
            toast.error('Failed to add members');
        }
    };

    const handleRemoveMember = async (groupId, memberId) => {
        try {
            await onRemoveMember(groupId, memberId);
            
            // Update the local state immediately
            setSelectedGroup(prev => {
                if (!prev || prev.id !== groupId) return prev;
                return {
                    ...prev,
                    members: prev.members.filter(m => m.id !== memberId)
                };
            });
            
            toast.success('Member removed successfully');
            
            // Refresh in background
            onRefresh?.();
        } catch (error) {
            console.error('Error removing member:', error);
            toast.error('Failed to remove member');
        }
    };

    const handleInviteMember = async () => {
        if (!selectedGroup || !emailInvite) return;

        try {
            await axiosInstance.post(`/group-invitations/invite`, {
                email: emailInvite,
                groupId: selectedGroup.id,
                inviterId: user.id,
                teamId: selectedGroup.teamId
            });

            // Clear the email input
            setEmailInvite('');
            
            // Refresh groups data
            await onRefresh?.();
            
            toast.success('Invitation sent successfully');
        } catch (error) {
            console.error('Error sending invitation:', error);
            toast.error('Failed to send invitation');
        }
    };

    const handleEditClick = (group) => {
        setEditingGroup(group);
        setEditDialogOpen(true);
    };

    const handleEditSubmit = () => {
        if (newGroupName.trim() && editingGroup) {
            onEditGroup(editingGroup.id, {
                name: newGroupName.trim(),
                description: newGroupDescription.trim()
            });
            setNewGroupName('');
            setNewGroupDescription('');
            setEditMode(false);
            setEditingGroup(null);
            setOpen(false);
        }
    };

    const renderMemberSelection = () => {
        if (!availableUsers?.length) {
            return (
                <Typography color="textSecondary">
                    No available users to add
                </Typography>
            );
        }

        return (
            <Autocomplete
                options={availableUsers}
                getOptionLabel={(option) => `${option.name} (${option.email})`}
                onChange={(event, newValue) => setSelectedUser(newValue)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Select User"
                        variant="outlined"
                        fullWidth
                    />
                )}
            />
        );
    };

    const renderMembers = (members) => {
        return members?.map((member) => (
            <ListItem key={member.id}>
                <ListItemText
                    primary={`${member.first_name} ${member.last_name}`}
                    secondary={member.email}
                />
                <IconButton 
                    edge="end" 
                    aria-label="delete"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveMember(selectedGroup.id, member.id);
                    }}
                >
                    <DeleteIcon />
                </IconButton>
            </ListItem>
        ));
    };

    const renderGroupMemberSelection = (group) => {
        if (!group) return null;

        // Filter out users who are already members of this group
        const currentMemberIds = new Set(selectedGroup?.members?.map(m => m.id) || []);
        const availableMembersForGroup = availableUsers.filter(
            user => !currentMemberIds.has(user.id)
        );

        if (availableMembersForGroup.length === 0) {
            return (
                <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    No available users to add to this group
                </Typography>
            );
        }

        return (
            <Box>
                <Autocomplete
                    multiple
                    value={selectedUsers[group.id] || []}
                    onChange={(event, newValue) => {
                        setSelectedUsers(prev => ({
                            ...prev,
                            [group.id]: newValue
                        }));
                    }}
                    options={availableMembersForGroup}
                    getOptionLabel={(option) => 
                        `${option.first_name} ${option.last_name} (${option.email})`
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Select Members"
                            placeholder="Search members"
                        />
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                key={option.id}
                                label={`${option.first_name} ${option.last_name}`}
                                {...getTagProps({ index })}
                            />
                        ))
                    }
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleAddMembers(group.id)}
                    disabled={!(selectedUsers[group.id]?.length > 0)}
                    sx={{ mt: 2 }}
                    fullWidth
                >
                    Add Selected Members
                </Button>
            </Box>
        );
    };

    const renderGroupMembers = (group) => {
        if (!group.members || group.members.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    No members
                </Typography>
            );
        }

        return (
            <List dense>
                {group.members.map((member) => (
                    <ListItem 
                        key={member.id}
                        secondaryAction={
                            isTeamLeader && (
                                <IconButton 
                                    edge="end" 
                                    aria-label="remove"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveMember(group.id, member.id);
                                    }}
                                >
                                    <RemoveCircleIcon color="error" />
                                </IconButton>
                            )
                        }
                    >
                        <ListItemAvatar>
                            <Avatar>
                                {`${member.first_name[0]}${member.last_name[0]}`}
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText 
                            primary={`${member.first_name} ${member.last_name}`}
                            secondary={member.email}
                        />
                    </ListItem>
                ))}
            </List>
        );
    };

    return (
        <Box>
            <Grid container spacing={3}>
                {groups.map((group) => (
                    <Grid item xs={12} sm={6} md={4} key={group.id}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {group.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {group.description}
                                </Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Members: {group.members?.length || 0}
                                    </Typography>
                                </Box>
                            </CardContent>
                            <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
                                <IconButton 
                                    size="small"
                                    onClick={() => handleOpenMemberDialog(group)}
                                    title="Manage Members"
                                >
                                    <GroupIcon />
                                </IconButton>
                                <IconButton 
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditClick(group);
                                    }}
                                    title="Edit Group"
                                >
                                    <EditIcon />
                                </IconButton>
                                <IconButton 
                                    size="small"
                                    onClick={() => onDeleteGroup(group.id)}
                                    title="Delete Group"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </CardActions>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Create Group Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)}>
                <DialogTitle>Create New Group</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Group Name"
                        fullWidth
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                    />
                    <TextField
                        margin="dense"
                        label="Description"
                        fullWidth
                        multiline
                        rows={2}
                        value={newGroupDescription}
                        onChange={(e) => setNewGroupDescription(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={handleSubmit} color="primary">
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Member Management Dialog */}
            <Dialog 
                open={memberDialogOpen} 
                onClose={handleCloseMemberDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Manage Members - {selectedGroup?.name}
                </DialogTitle>
                <DialogContent>
                    <Tabs
                        value={tabValue}
                        onChange={(e, newValue) => setTabValue(newValue)}
                        sx={{ mb: 2 }}
                    >
                        <Tab label="Current Members" />
                        <Tab label="Add Members" />
                        <Tab label="Invite by Email" />
                        <Tab label="Pending Invitations" />
                    </Tabs>

                    {tabValue === 0 && (
                        <List>
                            {selectedGroup?.members?.map((member) => (
                                <ListItem 
                                    key={member.id}
                                    secondaryAction={
                                        isTeamLeader && (
                                            <IconButton 
                                                edge="end" 
                                                aria-label="remove"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveMember(selectedGroup.id, member.id);
                                                }}
                                            >
                                                <RemoveCircleIcon color="error" />
                                            </IconButton>
                                        )
                                    }
                                >
                                    <ListItemAvatar>
                                        <Avatar>
                                            {`${member.first_name[0]}${member.last_name[0]}`}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText 
                                        primary={`${member.first_name} ${member.last_name}`}
                                        secondary={member.email}
                                    />
                                </ListItem>
                            ))}
                            {(!selectedGroup?.members || selectedGroup.members.length === 0) && (
                                <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                                    No members in this group
                                </Typography>
                            )}
                        </List>
                    )}

                    {tabValue === 1 && (
                        <Box sx={{ p: 2 }}>
                            {renderGroupMemberSelection(selectedGroup)}
                        </Box>
                    )}

                    {tabValue === 2 && (
                        <Box sx={{ p: 2 }}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                value={emailInvite}
                                onChange={(e) => setEmailInvite(e.target.value)}
                                variant="outlined"
                            />
                            <Button
                                variant="contained"
                                startIcon={<EmailIcon />}
                                onClick={handleInviteMember}
                                disabled={!emailInvite}
                                sx={{ mt: 2 }}
                                fullWidth
                            >
                                Send Invitation
                            </Button>
                        </Box>
                    )}

                    {tabValue === 3 && (
                        <GroupInvitationManager 
                            groupId={selectedGroup?.id}
                            onUpdate={() => {
                                onRefresh?.();
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog 
                open={editDialogOpen} 
                onClose={() => {
                    setEditDialogOpen(false);
                    setEditingGroup(null);
                }}
            >
                <EditGroupForm
                    group={editingGroup}
                    onSubmit={async (data) => {
                        await onEditGroup(editingGroup.id, data);
                        setEditDialogOpen(false);
                        setEditingGroup(null);
                    }}
                    onCancel={() => {
                        setEditDialogOpen(false);
                        setEditingGroup(null);
                    }}
                />
            </Dialog>
        </Box>
    );
};

const UserGroupsList = ({ userId }) => {
    const [userGroups, setUserGroups] = useState([]);

    useEffect(() => {
        const fetchUserGroups = async () => {
            try {
                const response = await axiosInstance.get(`/users/${userId}/groups`);
                setUserGroups(response.data.groups);
            } catch (error) {
                console.error('Error fetching user groups:', error);
            }
        };

        if (userId) {
            fetchUserGroups();
        }
    }, [userId]);

    return (
        <Box>
            <Typography variant="subtitle2">Member of Groups:</Typography>
            <List dense>
                {userGroups.map(group => (
                    <ListItem key={group.id}>
                        <ListItemText primary={group.name} />
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};

export default GroupManagement; 